<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Earth mesh"
          >
            An Earth mesh is a rectangular grid of longitudes and latitudes. A
            constellation performance analysis will compute revisit statistics
            on each point of the chosen mesh.
            <br />
            <br />
            When designing a mesh, keep in mind that:
            <ul>
              <li>
                For a constant area of interest, decreasing the number of points
                will decrease the simulation runtime. It will also decrease the
                resolution of the revisit maps.
              </li>
              <li>
                For a constant number of points in the mesh, reducing the area
                of interest will decrease the simulation runtime. It will also
                increase the resolution of the revisit maps.
              </li>
            </ul>
            A good advice would be to start with a 10 x 10 mesh to avoid very
            long simulations.
          </exo-help-dialog>
        </template>
        <v-row>
          <v-col cols="5" class="form-column">
            <exo-input-name :root="_self" gt_PerformanceAnalysis-v-step="40" />
            <exo-input-select
              :root="_self"
              name="rangeType"
              gt_PerformanceAnalysis-v-step="41"
            >
            </exo-input-select>
            <exo-input-select
              :root="_self"
              name="elementDefinition"
              gt_PerformanceAnalysis-v-step="42"
            >
              <exo-help-dialog title="Mesh element definition">
                <tooltipEarthMeshElementDefinition />
              </exo-help-dialog>
            </exo-input-select>
            <v-divider></v-divider>
            <v-row>
              <v-col cols="6">
                <exo-input-double :root="_self" name="westernmostLongitude" />
              </v-col>
              <v-col cols="6" offset-md-1>
                <exo-input-double :root="_self" name="easternmostLongitude" />
              </v-col>
              <div gt_PerformanceAnalysis-v-step="43" />
              <exo-form-alert
                class="mb-2"
                type="error"
                v-if="isWestEqualToEast()"
              >
                Error: East and West cannot be equal
              </exo-form-alert>
              <v-col cols="6">
                <exo-input-double :root="_self" name="southernmostLatitude" />
              </v-col>
              <v-col cols="6" offset-md-1>
                <exo-input-double :root="_self" name="northernmostLatitude" />
              </v-col>
              <div gt_PerformanceAnalysis-v-step="44" />
              <exo-form-alert
                class="mb-2"
                type="error"
                v-if="!isNorthGreaterThanSouth()"
              >
                Error: North must be strictly greater than South
              </exo-form-alert>
            </v-row>
            <v-divider></v-divider>
            <v-row>
              <exo-form-alert
                type="info"
                v-if="
                  inputs.rangeType === 'STEP' &&
                  inputs.elementDefinition === 'CONSTANT_AREA'
                "
              >
                With the 'Constant area' definition, the 'Latitude step' field
                corresponds to the maximum latitude step desired (found in the
                highest latitudes).
              </exo-form-alert>
              <v-col cols="6" offset-md-1>
                <exo-input-double :root="_self" name="numberOfLongitudes" />
                <exo-input-double :root="_self" name="rangeStepLongitude" />
                <exo-form-alert
                  class="mb-2"
                  type="error"
                  v-if="!isLongitudeStepCorrect()"
                >
                  Error: longitude step > East - West
                </exo-form-alert>
                <exo-form-alert
                  class="mb-2"
                  type="error"
                  v-if="isMaxNumberOfLonExceeded()"
                >
                  Error: maximal number of longitude steps (500) exceeded
                </exo-form-alert>
              </v-col>
              <v-col cols="6" offset-md-1>
                <exo-input-double :root="_self" name="numberOfLatitudes" />
                <exo-input-double :root="_self" name="rangeStepLatitude" />
                <exo-form-alert
                  class="mb-2"
                  type="error"
                  v-if="!isLatitudeStepCorrect()"
                >
                  Error: latitude step > North - South
                </exo-form-alert>
                <exo-form-alert
                  class="mb-2"
                  type="error"
                  v-if="isMaxNumberOfLatExceeded()"
                >
                  Error: maximal number of latitude steps (500) exceeded
                </exo-form-alert>
              </v-col>
              <div gt_PerformanceAnalysis-v-step="45" />
            </v-row>
            <v-row>
              <span>
                Hold <kbd>Shift + Left Click</kbd> to draw a rectangle on the
                globe
              </span>
            </v-row>
            <div gt_PerformanceAnalysis-v-step="47" />
          </v-col>
          <v-col cols="7">
            <cesium-viewer
              :mesh="inputData"
              :points="meshPoints"
              v-on:coordinatesChanged="updateCoordinates"
              ref="cesiumViewer"
              gt_PerformanceAnalysis-v-step="46"
            />
            <exo-form-alert
              class="mb-2"
              type="warning"
              v-if="tooManyPointsToDisplay()"
            >
              Warning: too many points to display on the earth mesh viewer, you
              can still save this mesh
            </exo-form-alert>
          </v-col>
        </v-row>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
    <v-tour
      name="performanceAnalysisTourEarthMesh"
      :steps="steps_earth_mesh"
      :callbacks="customCallbacks"
    ></v-tour>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import constellationComponentsLoader from '../../components/Forms/analyticalConstellationsComponents.js'
import { mapGetters, mapActions } from 'vuex'
import dataTools from '../../tools/data'
import fieldsDescription from '../../tools/fieldsDescription'
import EarthMeshViewer from './EarthMeshViewer.vue'
import tooltipEarthMeshElementDefinition from '../../components/Tooltips/ConstellationModule/tooltipEarthMeshElementDefinition'
import guidedTutorialPerformanceAnalysis, {
  steps_earth_mesh
} from '../../components/GuidedTutorials/guidedTutorialPerformanceAnalysis.js'
import './formsStyle.css'
constellationComponentsLoader()
export default {
  name: 'AnalyticalConstellationEarthMesh',

  mixins: [formMixin, formAnalyticalMixin],
  components: {
    tooltipEarthMeshElementDefinition,
    'cesium-viewer': EarthMeshViewer
  },

  mounted() {
    if (this.gt_PerformanceAnalysis.isActive) {
      this.$tours['performanceAnalysisTourEarthMesh'].start()
    }
    if (!this.paramsValid) {
      return
    }
    let { name, description, ...inputData } = this.inputs
    this.computeEarthMesh().then(() => {
      this.inputData = inputData
    })
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'westernmostLongitude',
          text: 'West' + this.getDefaultUnitLabel('westernmostLongitude'),
          sortable: true
        },
        {
          value: 'easternmostLongitude',
          text: 'East' + this.getDefaultUnitLabel('easternmostLongitude'),
          sortable: true
        },
        {
          value: 'numberOfLongitudes',
          text: 'Number of longitudes',
          sortable: true
        },
        {
          value: 'southernmostLatitude',
          text: 'South' + this.getDefaultUnitLabel('southernmostLatitude'),
          sortable: true
        },
        {
          value: 'northernmostLatitude',
          text: 'North' + this.getDefaultUnitLabel('northernmostLatitude'),
          sortable: true
        },
        {
          value: 'numberOfLatitudes',
          text: 'Number of latitudes',
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,

      enumPropertiesList: this.getEnumProperties(),
      inputData: null,
      steps_earth_mesh: steps_earth_mesh,
      customCallbacks: {
        onNextStep: (currentStep) => {
          guidedTutorialPerformanceAnalysis.onNextStepEarthMesh(
            currentStep,
            this
          )
        },
        onSkip: () => {
          guidedTutorialPerformanceAnalysis.onSkipEarthMesh(this)
        },
        onFinish: () => {
          guidedTutorialPerformanceAnalysis.onFinishSubmit(this)
        }
      },

      inputs: {
        southernmostLatitude: this.getDefaultValue('southernmostLatitude'),
        northernmostLatitude: this.getDefaultValue('northernmostLatitude'),
        numberOfLatitudes: this.getDefaultValue('numberOfLatitudes'),
        westernmostLongitude: this.getDefaultValue('westernmostLongitude'),
        easternmostLongitude: this.getDefaultValue('easternmostLongitude'),
        numberOfLongitudes: this.getDefaultValue('numberOfLongitudes'),
        rangeType: this.getDefaultValue('rangeType'),
        elementDefinition: this.getDefaultValue('elementDefinition'),
        rangeStepLatitude: this.getDefaultValue('rangeStepLatitude'),
        rangeStepLongitude: this.getDefaultValue('rangeStepLongitude'),
        scope: this.getDefaultValue('scope')
      },
      meshPoints: []
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),
    getObjectSubType(instance) {
      let types = { rangeType: instance.rangeType }
      return types
    },

    getRequiredPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getObjectSubType(instance),
        'required'
      )
    },

    getOptionalPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        this.getObjectSubType(instance),
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    getCurrentFormTitle() {
      return 'Earth Mesh'
    },

    getCurrentFormType() {
      return 'EarthMeshDefinition'
    },

    isLongitudeStepCorrect() {
      let easternmost = Number(this.inputs.easternmostLongitude)
      let westernmost = Number(this.inputs.westernmostLongitude)
      if (
        this.inputs.rangeType === 'STEP' &&
        this.inputs.rangeStepLongitude != 0
      ) {
        if (easternmost > westernmost) {
          if (this.inputs.rangeStepLongitude > easternmost - westernmost) {
            return false
          }
        } else if (easternmost < westernmost) {
          if (
            this.inputs.rangeStepLongitude >
            360 + easternmost - westernmost
          ) {
            return false
          }
        }
      }
      return true
    },

    isMaxNumberOfLonExceeded() {
      let easternmost = Number(this.inputs.easternmostLongitude)
      let westernmost = Number(this.inputs.westernmostLongitude)
      let numberOfLon = 0
      if (this.inputs.rangeType === 'STEP') {
        if (
          this.inputs.rangeStepLongitude === '0' ||
          this.inputs.rangeStepLongitude === ''
        ) {
          return false
        }
        numberOfLon =
          (easternmost - westernmost) / this.inputs.rangeStepLongitude
      }
      return numberOfLon > 500 ? true : false
    },

    isMaxNumberOfLatExceeded() {
      let northernmost = Number(this.inputs.northernmostLatitude)
      let southernmost = Number(this.inputs.southernmostLatitude)
      let numberOfLat = 0
      if (this.inputs.rangeType === 'STEP') {
        if (
          this.inputs.rangeStepLatitude === '0' ||
          this.inputs.rangeStepLatitude === ''
        ) {
          return false
        }
        numberOfLat =
          (northernmost - southernmost) / this.inputs.rangeStepLatitude
      }
      return numberOfLat > 500 ? true : false
    },

    isNorthGreaterThanSouth() {
      if (
        this.inputs.northernmostLatitude !== '' &&
        this.inputs.southernmostLatitude !== ''
      ) {
        let northernmost = Number(this.inputs.northernmostLatitude)
        let southernmost = Number(this.inputs.southernmostLatitude)
        if (northernmost <= southernmost) {
          return false
        }
      }
      return true
    },

    isWestEqualToEast() {
      if (
        this.inputs.westernmostLongitude === '' ||
        this.inputs.easternmostLongitude === ''
      ) {
        return false
      }
      return (
        Number(this.inputs.westernmostLongitude) ===
        Number(this.inputs.easternmostLongitude)
      )
    },

    isLatitudeStepCorrect() {
      let northernmost = Number(this.inputs.northernmostLatitude)
      let southernmost = Number(this.inputs.southernmostLatitude)
      if (
        this.inputs.rangeType == 'STEP' &&
        this.inputs.rangeStepLatitude > Math.abs(northernmost - southernmost)
      ) {
        return false
      }
      return true
    },
    // avoid crashing the front when too many longitude/latitude points to display
    tooManyPointsToDisplay() {
      return (
        this.meshPoints == null ||
        (this.inputs.rangeType == 'NUMBER' &&
          this.meshPoints.length >
            EarthMeshViewer.data().maxMeshPointsNumber) ||
        (this.inputs.rangeType == 'STEP' &&
          this.meshPoints.length > EarthMeshViewer.data().maxMeshPointsNumber)
      )
    },

    updateCoordinates(coordinates) {
      this.inputs.southernmostLatitude = coordinates.south.toFixed(4)
      this.inputs.northernmostLatitude = coordinates.north.toFixed(4)
      this.inputs.westernmostLongitude = coordinates.west.toFixed(4)
      this.inputs.easternmostLongitude = coordinates.east.toFixed(4)
    },

    async computeEarthMesh() {
      let data = await this.$http
        .post('/physics/getearthmeshcoordinates', this.createObjectFromForm())
        .then(function (response) {
          return response.data
        })
        .catch((e, __) => {
          this.errorTrace = this.getErrorMessage(e)
          return null
        })
      this.meshPoints = data
    }
  },

  computed: {
    ...mapGetters(['currentMission', 'gt_PerformanceAnalysis'])
  },

  watch: {
    inputs: {
      async handler() {
        if (!this.paramsValid) {
          return
        }
        window.setTimeout(() => {
          let { name, description, ...inputData } = this.inputs
          this.computeEarthMesh().then(() => {
            this.inputData = inputData
          })
        }, 1000)
      },
      deep: true
    },
    'inputs.displayForm': {
      handler(show) {
        if (!show) this.$refs.cesiumViewer.removeAllPrimitives()
      }
    }
  }
}
</script>
